import storage from '@mylo/webcore/es/utility/localStorage'
import delve from 'dlv'
import { dset } from 'dset'
import qs from 'query-string'

import { applyCookies } from './cookies'
import convertToPaths from './convertToPaths'
import is from './utility/is'

export const LOAD = 'storage/LOAD'

export const loadState = ({
  mergeCurrentQueryParams = '',
  preferCurrentQueryParams = false,
  preferCachedOverCurrent = false,
  mergeCachedQueryParams = '',
  preferCachedQueryParams = false,
  mergeCookies = '',
  preferCookies = false,
  preferCookiesOverQueryParams = false,
  ...storageProps
}) => {
  if (is.agentUser()) {
    // always apply the current query string values against an empty state
    return applyCurrentQueryParams(mergeCurrentQueryParams, true)({})
  }

  let state = getCachedState(storageProps)
  let transforms = []
  if (mergeCurrentQueryParams) {
    transforms.push(
      applyCurrentQueryParams(mergeCurrentQueryParams, preferCurrentQueryParams)
    )
  }
  if (mergeCachedQueryParams) {
    transforms[preferCachedOverCurrent ? 'push' : 'unshift'](
      applyCachedQueryParams(mergeCachedQueryParams, preferCachedQueryParams)
    )
  }
  if (mergeCookies) {
    transforms[preferCookiesOverQueryParams ? 'push' : 'unshift'](
      applyCachedCookies(mergeCookies, preferCookies)
    )
  }

  return transforms
    .concat(syncSalesforceCookieAliases) // ensures sync on load
    .reduce((next, transform) => transform(next), state)
}

export const saveState = ({
  state,
  storageKey = 'state',
  storageWhitelist = [],
  storageBlacklist = [],
  storageDebug = false
}) => {
  try {
    const serializedState = is.agentUser()
      ? {}
      : blacklist(whitelist(state, storageWhitelist), storageBlacklist)
    if (storageDebug) {
      console.log(`Saving state to "${storageKey}": ${serializedState}`)
    }
    storage.set(storageKey, serializedState)
  } catch (err) {
    if (storageDebug) {
      console.error(`Failed to save state to "${storageKey}"`, err)
    }
  }
}

function getCachedState ({
  storageKey = 'state',
  storageBlacklist = [],
  storageDebug = false
}) {
  try {
    const state = storage.get(storageKey)
    if (!state) {
      if (storageDebug) {
        console.log(`No state loaded from "${storageKey}"`)
      }
      return {}
    }
    if (typeof state !== 'object') {
      if (storageDebug) {
        console.warn(`State loaded from "${storageKey}" is not an object`)
      }
      return {}
    }
    return blacklist(state, storageBlacklist)
  } catch (err) {
    if (storageDebug) {
      console.error(`Failed to load cached state from "${storageKey}"`, err)
    }
    return {}
  }
}

function toPath (list) {
  return Array.isArray(list) ? list.join('.') : list.toString()
}

function whitelist (state, list) {
  if (!Array.isArray(list)) {
    return state
  }

  const values = list.map(toPath).reduce((values, path) => {
    const value = delve(state, path)
    if (value != null) {
      return values.concat({ path, value })
    }
    return values
  }, [])

  return values.reduce((nextState, { path, value }) => {
    dset(nextState, path, value)
    return nextState
  }, {})
}

function blacklist (state, list) {
  if (!Array.isArray(list)) {
    return state
  }

  return list.map(toPath).reduce((nextState, path) => {
    dset(nextState, path, undefined)
    return nextState
  }, state)
}

function applyCurrentQueryParams (prefix, prefer) {
  return function currentQueryParamsApplier (state) {
    const queryParams = qs.parse(window.location.search)
    Object.keys(queryParams).map(key => {
      if (key[0] === key[0].toLocaleUpperCase()) {
        dset(
          queryParams,
          '' + key[0].toLocaleLowerCase() + key.slice(1),
          queryParams[key]
        )
        delete queryParams[key]
      }
    })
    const pathsObj = convertToPaths(queryParams, prefix || void 0)

    return applyChanges(state, pathsObj, prefer)
  }
}

function applyCachedQueryParams (prefix, prefer) {
  return function cachedQueryParamsApplier (state) {
    const queryParams = storage.get('mylo-query-params') || {}
    const pathsObj = convertToPaths(queryParams, prefix || void 0)

    return applyChanges(state, pathsObj, prefer)
  }
}

function applyCachedCookies (prefix, prefer) {
  return function cachedCookiesApplier (state) {
    const cookies = applyCookies({ payload: {} })
    const pathsObj = Object.keys(cookies)
      .filter(name => cookies[name] != null) // drop any null/undefined values
      .reduce(
        (out, name) => ({
          ...out,
          [prefix ? `${prefix}.${name}` : name]: cookies[name]
        }),
        {}
      )

    return applyChanges(state, pathsObj, prefer)
  }
}

function applyChanges (state, pathsObj, prefer) {
  Object.keys(pathsObj).forEach(path => {
    const existingValue = delve(state, path)
    const value = prefer || !existingValue ? pathsObj[path] : existingValue
    dset(state, path, value)
  })

  return state
}

function syncSalesforceCookieAliases (state) {
  // flatten the nested object into a flat
  // object of dot-notation paths
  const pathsObj = convertToPaths(state)
  let nextState = {}

  Object.keys(pathsObj).forEach(path => {
    // extract the value from the current state
    const value = delve(state, path)

    // copy the value into the new state
    dset(nextState, path, value)

    // aliased props will still need to be set within
    // the same initial path of the new state
    const [prop, ...rest] = path.split('.').reverse()
    const initialPath = rest.reverse().join('.')

    // determine if there are any aliases
    // that should be set
    const alias = {
      externalLeadId: 'sflcid',
      externalAccountId: 'sfacid',
      externalOpportunityId: 'sfocid',
      externalQuoteId: 'sfqcid',
      sflcid: 'externalLeadId',
      sfacid: 'externalAccountId',
      sfocid: 'externalOpportunityId',
      sfqcid: 'externalQuoteId'
    }[prop]

    if (alias) {
      // tack on the new alias
      const aliasPath = `${initialPath}.${alias}`

      // apply the same value at the aliased path
      dset(nextState, aliasPath, value)
    }
  })

  return nextState
}
